/**
 * Sequence step evaluation error triage service.
 *
 * Backs the /sequences/triage UI. Reads sequence_step_evaluation_errors
 * (RLS-gated to admin/superadmin/manager per migration
 * 2026-05-17-001) and invokes the existing resolve_sequence_step_
 * evaluation_errors RPC (SECURITY DEFINER, same role gate, OR
 * service_role).
 *
 * Errors are grouped by (enrollment_id, step_id) in the UI — see
 * groupErrors() below. The resolve action takes both ids so it only
 * clears the step in question (not other errors on the same
 * enrollment, which audit-AI flagged as a footgun).
 */

import { supabase } from "./supabase";
import type { UUID } from "../types/common.types";

export interface SequenceStepEvaluationError {
  id: UUID;
  enrollment_id: UUID;
  step_id: UUID;
  contact_id: UUID;
  list_id: UUID | null;
  child_step_id: UUID | null;
  condition_index: number | null;
  error_code: string;
  error_message: string | null;
  occurred_at: string;
  resolved_at: string | null;

  // Joined display fields (populated by listSequenceStepEvaluationErrors)
  contact?: {
    id: UUID;
    first_name: string | null;
    last_name: string | null;
    email: string | null;
  } | null;
  enrollment?: {
    id: UUID;
    sequence_id: UUID | null;
    sequence?: {
      id: UUID;
      name: string;
    } | null;
  } | null;
  step?: {
    id: UUID;
    name: string | null;
    step_type: string | null;
  } | null;
}

export interface SequenceErrorFilters {
  status?: "unresolved" | "resolved" | "all";
  sequence_id?: UUID | null;
  error_code?: string | null;
  /** ISO date string — only return errors with occurred_at >= this. */
  since?: string | null;
}

export interface SequenceErrorPagination {
  page: number;
  limit: number;
}

/**
 * Group key for the triage UI: (enrollment_id, step_id). Matches the
 * arguments the resolve RPC takes so a "Resolve all in group" button
 * resolves exactly the displayed errors (no scope creep onto other
 * steps of the same enrollment).
 */
export interface SequenceErrorGroup {
  enrollment_id: UUID;
  step_id: UUID;
  contact: SequenceStepEvaluationError["contact"];
  enrollment: SequenceStepEvaluationError["enrollment"];
  step: SequenceStepEvaluationError["step"];
  errors: SequenceStepEvaluationError[];
  newest_occurred_at: string;
  total_error_count: number;
}

/**
 * Paginated read with display joins. Returns raw rows; UI calls
 * groupErrors() to fold by (enrollment_id, step_id).
 */
export async function listSequenceStepEvaluationErrors(
  filters: SequenceErrorFilters,
  pagination: SequenceErrorPagination,
): Promise<{ data: SequenceStepEvaluationError[]; total: number }> {
  let query = supabase
    .from("sequence_step_evaluation_errors")
    .select(
      `
      id, enrollment_id, step_id, contact_id, list_id, child_step_id,
      condition_index, error_code, error_message, occurred_at, resolved_at,
      contact:contacts!sequence_step_evaluation_errors_contact_id_fkey(
        id, first_name, last_name, email
      ),
      enrollment:sequence_enrollments!sequence_step_evaluation_errors_enrollment_id_fkey(
        id, sequence_id,
        sequence:message_sequences!sequence_enrollments_sequence_id_fkey(id, name)
      ),
      step:message_sequence_steps!sequence_step_evaluation_errors_step_id_fkey(
        id, name, step_type
      )
    `,
      { count: "exact" },
    )
    .order("occurred_at", { ascending: false });

  const status = filters.status ?? "unresolved";
  if (status === "unresolved") {
    query = query.is("resolved_at", null);
  } else if (status === "resolved") {
    query = query.not("resolved_at", "is", null);
  }
  // status === "all" → no resolved_at filter

  if (filters.sequence_id) {
    // Join filter: enrollment.sequence_id. PostgREST nested filter syntax.
    query = query.eq("enrollment.sequence_id", filters.sequence_id);
  }
  if (filters.error_code) {
    query = query.eq("error_code", filters.error_code);
  }
  if (filters.since) {
    query = query.gte("occurred_at", filters.since);
  }

  const from = (pagination.page - 1) * pagination.limit;
  const to = from + pagination.limit - 1;
  query = query.range(from, to);

  const { data, error, count } = await query;
  if (error) {
    throw new Error(
      `listSequenceStepEvaluationErrors failed: ${error.message}`,
    );
  }
  return {
    data: (data ?? []) as unknown as SequenceStepEvaluationError[],
    total: count ?? 0,
  };
}

/**
 * Fold a flat error list into per-(enrollment_id, step_id) groups.
 * Groups inherit display fields from the first row in the group; the
 * occurrence count is the literal row count (each row is already
 * dedup'd at log time by the partial unique index).
 */
export function groupErrors(
  rows: SequenceStepEvaluationError[],
): SequenceErrorGroup[] {
  const groupMap = new Map<string, SequenceErrorGroup>();
  for (const row of rows) {
    const key = `${row.enrollment_id}:${row.step_id}`;
    if (!groupMap.has(key)) {
      groupMap.set(key, {
        enrollment_id: row.enrollment_id,
        step_id: row.step_id,
        contact: row.contact,
        enrollment: row.enrollment,
        step: row.step,
        errors: [],
        newest_occurred_at: row.occurred_at,
        total_error_count: 0,
      });
    }
    const group = groupMap.get(key)!;
    group.errors.push(row);
    group.total_error_count += 1;
    if (row.occurred_at > group.newest_occurred_at) {
      group.newest_occurred_at = row.occurred_at;
    }
  }
  return Array.from(groupMap.values()).sort((a, b) =>
    b.newest_occurred_at.localeCompare(a.newest_occurred_at),
  );
}

/**
 * Resolve all unresolved errors for a specific (enrollment, step).
 * Audit-AI required this to be step-scoped — calling with p_step_id =
 * NULL would resolve hidden errors on OTHER steps of the same
 * enrollment.
 *
 * Side effect: the RPC sets evaluation_wait_until = NOW() on the
 * enrollment so cron rehydrates it on the next tick.
 *
 * Returns the count of error rows the RPC marked resolved.
 */
export async function resolveSequenceStepErrorGroup(
  enrollmentId: UUID,
  stepId: UUID,
): Promise<number> {
  const { data, error } = await supabase.rpc(
    "resolve_sequence_step_evaluation_errors",
    {
      p_enrollment_id: enrollmentId,
      p_step_id: stepId,
    },
  );
  if (error) {
    throw new Error(`resolveSequenceStepErrorGroup failed: ${error.message}`);
  }
  return (data as number) ?? 0;
}

/**
 * Lightweight unresolved-count for the badge on the Sequences list
 * page header. Uses a count-only query (no row data).
 */
export async function getUnresolvedSequenceErrorCount(): Promise<number> {
  const { count, error } = await supabase
    .from("sequence_step_evaluation_errors")
    .select("id", { count: "exact", head: true })
    .is("resolved_at", null);
  if (error) {
    // Non-admin roles will hit RLS denial here. Treat as zero — they
    // wouldn't see the badge link anyway because of the role-gated
    // route, but be defensive against console-noise.
    return 0;
  }
  return count ?? 0;
}

/**
 * Distinct error_code values for the filter dropdown.
 */
export async function getDistinctErrorCodes(
  filters: Pick<SequenceErrorFilters, "status">,
): Promise<string[]> {
  let query = supabase
    .from("sequence_step_evaluation_errors")
    .select("error_code");
  const status = filters.status ?? "unresolved";
  if (status === "unresolved") {
    query = query.is("resolved_at", null);
  } else if (status === "resolved") {
    query = query.not("resolved_at", "is", null);
  }
  const { data, error } = await query;
  if (error) {
    return [];
  }
  const codes = new Set<string>();
  for (const row of (data ?? []) as Array<{ error_code: string }>) {
    if (row.error_code) {
      codes.add(row.error_code);
    }
  }
  return Array.from(codes).sort();
}

/**
 * Distinct sequences (id + name) that have errors matching the filter.
 * Used by the filter dropdown so operators see only relevant choices.
 */
export async function getSequencesWithErrors(
  filters: Pick<SequenceErrorFilters, "status">,
): Promise<Array<{ id: UUID; name: string }>> {
  let query = supabase.from("sequence_step_evaluation_errors").select(
    `enrollment:sequence_enrollments!sequence_step_evaluation_errors_enrollment_id_fkey(
        sequence:message_sequences!sequence_enrollments_sequence_id_fkey(id, name)
      )`,
  );
  const status = filters.status ?? "unresolved";
  if (status === "unresolved") {
    query = query.is("resolved_at", null);
  } else if (status === "resolved") {
    query = query.not("resolved_at", "is", null);
  }
  const { data, error } = await query;
  if (error) {
    return [];
  }
  const seen = new Map<string, string>();
  for (const row of (data ?? []) as Array<{
    enrollment?: { sequence?: { id: string; name: string } | null } | null;
  }>) {
    const seq = row.enrollment?.sequence;
    if (seq && !seen.has(seq.id)) {
      seen.set(seq.id, seq.name);
    }
  }
  return Array.from(seen.entries())
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}
